import styled, { css } from "styled-components";
import MuiButton from "@mui/material/Button";

import type { AppButtonProps, ButtonBackground } from "../components/Button";

type StyledButtonProps = Pick<
  AppButtonProps,
  "$background" | "$bgcolor" | "$width" | "$slim" | "variant" | "disabled"
>;

const DEFAULT_COLOR = "#FD6520";
const HOVER_COLOR = "#FF5333";
const FILLED_HOVER_BG = "#FD6520";
const EMPTY_HOVER_BG = "rgba(241, 144, 33, 0.1)";
const DISABLED_TEXT = "rgba(0, 0, 0, 0.38)";
const DISABLED_BORDER = "rgba(0, 0, 0, 0.12)";
const DISABLED_BG = "rgba(0, 0, 0, 0.12)";
const BORDER_RADIUS = "30px";

function resolveMainColor(bgcolor?: string) {
  return bgcolor || DEFAULT_COLOR;
}

function resolveTextColor(
  variant: AppButtonProps["variant"] = "contained",
  background: ButtonBackground = "fill",
  bgcolor?: string,
  disabled?: boolean
) {
  if (disabled) return DISABLED_TEXT;

  if (variant === "contained" || background === "fill") {
    return "#FFFFFF";
  }

  return resolveMainColor(bgcolor);
}

function resolveBackgroundColor(
  background: ButtonBackground = "fill",
  bgcolor?: string,
  disabled?: boolean
) {
  if (disabled) return DISABLED_BG;

  return background === "fill" ? resolveMainColor(bgcolor) : "transparent";
}

function resolveBorder(
  variant: AppButtonProps["variant"] = "contained",
  background: ButtonBackground = "fill",
  bgcolor?: string,
  disabled?: boolean
) {
  if (disabled) {
    return `1px solid ${DISABLED_BORDER}`;
  }

  if (variant === "text") {
    return "none";
  }

  return `2px solid ${resolveMainColor(bgcolor)}`;
}

function resolveHoverBackground(
  background: ButtonBackground = "fill",
  bgcolor?: string
) {
  return background === "fill"
    ? bgcolor || FILLED_HOVER_BG
    : EMPTY_HOVER_BG;
}

function resolveHoverColor(
  background: ButtonBackground = "fill",
  bgcolor?: string
) {
  return background === "fill" ? "#FFFFFF" : resolveMainColor(bgcolor);
}

export const BaseButton = styled(MuiButton)<StyledButtonProps>`
  && {
    font-family: "Figtree", "Roboto", "sans-serif";
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
    text-transform: none;
    box-shadow: none;

    width: ${({ $width = "100%" }) => $width};
    padding: ${({ $slim }) => ($slim ? "5px 20px" : "10px 20px")};
    border-radius: ${BORDER_RADIUS};

    color: ${({ variant, $background, $bgcolor, disabled }) =>
      resolveTextColor(variant, $background, $bgcolor, disabled)};

    background-color: ${({ $background, $bgcolor, disabled }) =>
      resolveBackgroundColor($background, $bgcolor, disabled)};

    border: ${({ variant, $background, $bgcolor, disabled }) =>
      resolveBorder(variant, $background, $bgcolor, disabled)};
  }

  &&:hover {
    box-shadow: none;
    color: ${({ disabled, $background, $bgcolor, variant }) =>
      disabled
        ? resolveTextColor(variant, $background, $bgcolor, disabled)
        : resolveHoverColor($background, $bgcolor)};

    background-color: ${({ disabled, $background, $bgcolor }) =>
      disabled
        ? resolveBackgroundColor($background, $bgcolor, disabled)
        : resolveHoverBackground($background, $bgcolor)};

    border: ${({ disabled, variant, $background, $bgcolor }) => {
      const isText = variant === "text"
          ? "none"
          : `1px solid ${resolveMainColor($bgcolor)}`;

      return disabled
        ? resolveBorder(variant, $background, $bgcolor, disabled)
        : isText;
    }
  }

  &&.Mui-disabled {
    color: ${DISABLED_TEXT};
    border-color: ${DISABLED_BORDER};
    background-color: ${({ variant }) =>
      variant === "text" ? "transparent" : DISABLED_BG};
  }

  ${({ variant }) =>
    variant === "text" &&
    css`
      && {
        min-width: auto;
      }
    `}
`;
